import { Box, Grid, Typography, Select, MenuItem, FormControl, InputLabel, Pagination } from '@mui/material'
import VehicleCard, { type InventoryCardSurface } from './components/VehicleCard'
import { FilterDrawer } from './components/FilterDrawer'
import { Vehicle } from '@otw/models/inventory'
import { useMediaQuery, useTheme } from '@mui/material'
import { useMemo } from 'react'
import { useAuth } from 'contexts/AuthContext'
import { calculateDealForVehicle } from 'hooks/graybook/calculateDealForVehicle'
import { useFormulasWithRules } from 'hooks/graybook/useFormulasWithRules'

interface InventoryCardViewProps {
  collectionName: 'master_inventory' | 'shield_inventory'
  vehicles: Vehicle[]
  onEditVehicle: (vehicleId: string, data: { [key: string]: any }) => void
  onDeleteClick: (vehicleId: string) => void
  onManagePhotosClick: (vehicle: Vehicle) => void
  onManageDocumentsClick: (vehicle: Vehicle) => void
  onOpenTimelineClick: (vehicle: Vehicle) => void
  onShowHideOnWebsiteClick: (vehicle: Vehicle) => void
  canModify?: boolean
  canViewPurchasePrice?: boolean
  canViewProfit?: boolean
  canViewProfitRealized?: boolean
  // Pagination props
  currentPage: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (itemsPerPage: number) => void
  // Filter components and state
  FilterUI?: React.ComponentType
  SortUI?: React.ComponentType
  CompactSortUI?: React.ComponentType
  activeFilters?: any[]
  FilterChip?: React.ComponentType<any>
  ResetChip?: React.ComponentType
  activeTruckId?: string | null
  activeSurface?: InventoryCardSurface | null
  onActiveSurfaceChange?: (truckId: string, surface: InventoryCardSurface | null) => void
}

const InventoryCardView = ({
  collectionName,
  vehicles,
  onEditVehicle,
  onDeleteClick,
  onManagePhotosClick,
  onManageDocumentsClick,
  onOpenTimelineClick,
  onShowHideOnWebsiteClick,
  canModify = false,
  canViewPurchasePrice = false,
  canViewProfit = false,
  canViewProfitRealized = false,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  // Filter props
  FilterUI,
  SortUI,
  CompactSortUI,
  activeFilters = [],
  FilterChip,
  ResetChip,
  activeTruckId = null,
  activeSurface = null,
  onActiveSurfaceChange,
}: InventoryCardViewProps) => {
  const theme = useTheme()
  const { userInfo } = useAuth()
  const { formulas } = useFormulasWithRules({})

  // Simple responsive layout logic
  const isXs = useMediaQuery(theme.breakpoints.only('xs'))

  // Calculate pagination
  const totalVehicles = vehicles.length
  const totalPages = Math.ceil(totalVehicles / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  
  // Get current page vehicles
  const currentPageVehicles = useMemo(() => {
    return vehicles.slice(startIndex, endIndex)
  }, [vehicles, startIndex, endIndex])

  const dealsById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof calculateDealForVehicle>>()
    currentPageVehicles.forEach(vehicle => {
      map.set(vehicle.id, calculateDealForVehicle(vehicle, formulas, userInfo?.isOwner === true))
    })
    return map
  }, [currentPageVehicles, formulas, userInfo?.isOwner])

  // Handle page change
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    onPageChange(page)
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Handle items per page change
  const handleItemsPerPageChange = (event: any) => {
    const newItemsPerPage = event.target.value as number
    onItemsPerPageChange(newItemsPerPage)
    onPageChange(1) // Reset to first page when changing items per page
  }



  // Mobile layout — page scroll, not an inner overflow box
  if (isXs) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Mobile Filter Controls - Removed sort from here, now in pagination */}

        {/* Active filter chips */}
        {activeFilters.length > 0 && FilterChip && ResetChip && (
          <Box sx={{ px: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {activeFilters.map(([key, value], index) => (
                <FilterChip key={`${key}-${value}-${index}`} filterKey={key} value={value} />
              ))}
              <ResetChip />
            </Box>
          </Box>
        )}

        {/* Pagination Controls */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            px: 1,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {startIndex + 1}-{Math.min(endIndex, totalVehicles)} of {totalVehicles}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Sort UI for mobile - compact horizontal layout */}
            {CompactSortUI && (
              <Box sx={{ minWidth: 140 }}>
                <CompactSortUI />
              </Box>
            )}
            
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Per page</InputLabel>
              <Select
                value={itemsPerPage}
                label="Per page"
                onChange={handleItemsPerPageChange}
                MenuProps={{
                  disablePortal: false,
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      zIndex: 9999,
                    },
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Vehicle Grid */}
        <Box 
          sx={{ 
            px: 1,
          }}
        >
          <Grid container spacing={1} sx={{ margin: 0, width: '100%' }}>
            {currentPageVehicles.length === 0 ? (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                  No vehicles found
                </Typography>
              </Grid>
            ) : (
              currentPageVehicles.map(vehicle => (
                <Grid item key={vehicle.id} xs={12}>
                  <VehicleCard
                    collectionName={collectionName}
                    vehicle={vehicle}
                    onEditVehicle={data => onEditVehicle(vehicle.id, data)}
                    onDeleteClick={() => onDeleteClick(vehicle.id)}
                    onManagePhotosClick={() => onManagePhotosClick(vehicle)}
                    onManageDocumentsClick={() => onManageDocumentsClick(vehicle)}
                    onOpenTimelineClick={() => onOpenTimelineClick(vehicle)}
                    onShowHideOnWebsiteClick={() => onShowHideOnWebsiteClick(vehicle)}
                    isAdmin={canViewPurchasePrice && canViewProfit}
                    vehicles={currentPageVehicles}
                    canModify={canModify}
                    canViewPurchasePrice={canViewPurchasePrice}
                    canViewProfit={canViewProfit}
                    canViewProfitRealized={canViewProfitRealized}
                    deal={dealsById.get(vehicle.id) ?? null}
                    activeSurface={activeTruckId === vehicle.id ? activeSurface : null}
                    onActiveSurfaceChange={
                      onActiveSurfaceChange
                        ? surface => onActiveSurfaceChange(vehicle.id, surface)
                        : undefined
                    }
                  />
                </Grid>
              ))
            )}
          </Grid>
        </Box>

        {/* Bottom Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Mobile Filter Drawer - FAB on bottom left */}
        {FilterUI && SortUI && (
          <FilterDrawer 
            FilterUI={FilterUI} 
            SortUI={SortUI} 
          />
        )}
      </Box>
    )
  }

  // Desktop layout with sidebar — page scroll, not an inner overflow box
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
      }}
    >
      {/* Sidebar filters for desktop */}
      {FilterUI && (
        <Box sx={{ flexShrink: 0, mt: 0, height: 'fit-content' }}>
          <Box
            sx={{
              width: '300px',
              flexShrink: 0,
              position: 'sticky',
              top: 24,
              alignSelf: 'flex-start',
            }}
          >
            {/* Filter UI */}
            <FilterUI />
          </Box>
        </Box>
      )}

      {/* Main content area */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {/* Active filter chips */}
        {activeFilters.length > 0 && FilterChip && ResetChip && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, p: 1 }}>
            {activeFilters.map(([key, value], index) => (
              <FilterChip key={`${key}-${value}-${index}`} filterKey={key} value={value} />
            ))}
            <ResetChip />
          </Box>
        )}

        {/* Pagination Controls */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            px: 2,
            flexWrap: 'wrap',
            gap: 2,
            mb: 2
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {startIndex + 1}-{Math.min(endIndex, totalVehicles)} of {totalVehicles} vehicles
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Sort UI - Move to left of items per page and make it horizontal */}
            {CompactSortUI && (
              <Box sx={{ minWidth: 180 }}>
                <CompactSortUI />
              </Box>
            )}
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Items per page</InputLabel>
              <Select
                value={itemsPerPage}
                label="Items per page"
                onChange={handleItemsPerPageChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Vehicle Grid */}
        <Box sx={{ px: 2, minHeight: '400px' }}>
          <Grid container spacing={2} sx={{ margin: 0, width: '100%' }}>
            {currentPageVehicles.length === 0 ? (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                  No vehicles found
                </Typography>
              </Grid>
            ) : (
              currentPageVehicles.map(vehicle => (
                <Grid 
                  item 
                  key={vehicle.id} 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  lg={3}
                >
                  <VehicleCard
                    collectionName={collectionName}
                    vehicle={vehicle}
                    onEditVehicle={data => onEditVehicle(vehicle.id, data)}
                    onDeleteClick={() => onDeleteClick(vehicle.id)}
                    onManagePhotosClick={() => onManagePhotosClick(vehicle)}
                    onManageDocumentsClick={() => onManageDocumentsClick(vehicle)}
                    onOpenTimelineClick={() => onOpenTimelineClick(vehicle)}
                    onShowHideOnWebsiteClick={() => onShowHideOnWebsiteClick(vehicle)}
                    isAdmin={canViewPurchasePrice && canViewProfit}
                    vehicles={currentPageVehicles}
                    canModify={canModify}
                    canViewPurchasePrice={canViewPurchasePrice}
                    canViewProfit={canViewProfit}
                    canViewProfitRealized={canViewProfitRealized}
                    deal={dealsById.get(vehicle.id) ?? null}
                    activeSurface={activeTruckId === vehicle.id ? activeSurface : null}
                    onActiveSurfaceChange={
                      onActiveSurfaceChange
                        ? surface => onActiveSurfaceChange(vehicle.id, surface)
                        : undefined
                    }
                  />
                </Grid>
              ))
            )}
          </Grid>
        </Box>

        {/* Bottom Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default InventoryCardView
